import { QueryKeys, updateEntityFieldQuery } from '@common/queries/common';
import { Entity } from '@common/types/Entity';
import { EditBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Badges } from '@prophecy/ui-v3/Badges';
import { Button } from '@prophecy/ui-v3/Button';
import { Ellipsis } from '@prophecy/ui-v3/Ellipsis';
import { InlineInput } from '@prophecy/ui-v3/Input';
import { Stack } from '@prophecy/ui-v3/Layout';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { Display } from '@prophecy/ui-v3/Typography';
import { useDebounce, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { validateEntityName } from '@prophecy/utils/validation';
import { useQueryClient } from '@tanstack/react-query';
import { useGraphQlMutation } from 'frontend/core/src/data/util';
import { useState } from 'react';
import styled from 'styled-components';

const StyledInlineInput = styled(InlineInput)`
  input[type='text'] {
    padding-bottom: ${theme.spaces.x6};
    padding-top: ${theme.spaces.x6};
  }
`;
const StyledDisplay = styled(Display)`
  min-width: 0;
  max-width: 500px;
`;

const StyledVersion = styled.div`
  min-width: 0;
  max-width: 400px;
`;

export default function ProjectName({
  name,
  uid,
  latestAttemptedReleaseVersion
}: {
  name: string;
  uid: string;
  latestAttemptedReleaseVersion?: string;
}) {
  const [projectName, setProjectName] = useState(name);
  const [isEditing, setIsEditing] = useState(false);
  const mutation = useGraphQlMutation(updateEntityFieldQuery);
  const [isEditVisible, setIsEditVisible] = useState(false);
  const queryClient = useQueryClient();
  const reportError = async (error: string) => {
    await toast.error({ content: error });
    setInputValue(projectName);
    setIsEditing(false);
  };
  const onSubmit = usePersistentCallback(async () => {
    if (!inputValue || inputValue.trim().length === 0) {
      await reportError(`Update failed, project name is required.`);
      return;
    }
    const validationError = validateEntityName(inputValue);

    if (validationError) {
      await reportError(`Update failed, ${validationError}`);
      return;
    }
    if (projectName !== inputValue) {
      mutation.mutateAsync(
        {
          entityKind: Entity.Project,
          entityFieldName: 'name',
          entityFieldValue: inputValue,
          uid
        },
        {
          onSuccess: async () => {
            setProjectName(inputValue);
            await queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
          },
          onSettled: () => {
            setIsEditing(false);
            setIsEditVisible(false);
          }
        }
      );
    } else {
      setIsEditing(false);
      setIsEditVisible(false);
    }
  });
  const [inputValue, setInputValue] = useState(name);
  const onMouseLeave = useDebounce(() => setIsEditVisible(false), 200);

  return isEditing ? (
    <Stack gap={theme.spaces.x20} direction='horizontal' alignY='center'>
      <StyledInlineInput
        inputSize='l'
        value={inputValue}
        onChange={(value) => setInputValue(value)}
        autoFocus
        onBlur={onSubmit}
        onPressEnter={onSubmit}
        data-test-id='project-name-input'
      />
      {mutation.isLoading && <Spinner size='s' />}
    </Stack>
  ) : (
    <Stack
      onMouseEnter={() => setIsEditVisible(true)}
      onMouseLeave={onMouseLeave}
      alignY='center'
      direction='horizontal'
      width='100%'
      gap={theme.spaces.x20}>
      <StyledDisplay level='xs' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
        <Ellipsis tooltip={true}>{projectName}</Ellipsis>
      </StyledDisplay>
      <Button
        style={{ display: isEditVisible ? undefined : 'none' }}
        size='s'
        onClick={() => setIsEditing(true)}
        variant='linkGray'
        icon={<EditBIcon type='solid' />}
        data-test-id='project-name-edit-button'
        aria-label='Edit project name'
      />
      {latestAttemptedReleaseVersion && (
        <Badges style={{ display: isEditVisible ? 'none' : undefined }} size='s' tone='gray'>
          <StyledVersion>
            <Ellipsis tooltip={true}>{latestAttemptedReleaseVersion}</Ellipsis>
          </StyledVersion>
        </Badges>
      )}
    </Stack>
  );
}
